/**
 * Real-Time Notifications Hook
 * 
 * Custom hook for real-time notifications using Server-Sent Events (SSE):
 * - Connects to backend notification service
 * - Receives real-time notifications
 * - Tracks unread count
 * - Displays snackbar notifications
 * - Connection status tracking
 * 
 * @module hooks/useRealTimeNotifications
 * @param {string} userId - User ID for notification filtering
 * @returns {Object} Real-time notifications state and methods
 * @property {Array} notifications - Array of received notifications
 * @property {number} unreadCount - Number of unread notifications
 * @property {boolean} connected - Connection status
 */
import { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

export const useRealTimeNotifications = (userId) => {
  const { _t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);

  // Connect to real backend SSE endpoint
  useEffect(() => {
    if (!userId) return;

    const token = localStorage.getItem('wms_access_token');
    if (!token) return;

    // Use Server-Sent Events for real-time notifications
    const eventSource = new EventSource(
      `/api/v1/notifications/stream?userId=${userId}&token=${token}`
    );

    eventSource.onopen = () => {
      setConnected(true);
    };

    eventSource.onmessage = (event) => {
      try {
        const notification = JSON.parse(event.data);
        
        // Add to notifications list
        setNotifications(prev => [notification, ...prev].slice(0, 50));
        setUnreadCount(prev => prev + 1);
        
        // Show snackbar notification
        enqueueSnackbar(notification.message, {
          variant: notification.type || 'info',
          autoHideDuration: notification.priority === 'high' ? 10000 : 5000
        });
      } catch (error) { console.error(error); }
    };

    eventSource.onerror = () => {
      setConnected(false);
      eventSource.close();
    };

    return () => {
      eventSource.close();
      setConnected(false);
    };
  }, [userId, enqueueSnackbar]);

  // Mark notifications as read via API
  const markAsRead = useCallback(async (notificationIds) => {
    try {
      const response = await fetch('/api/v1/notifications/mark-read', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('wms_access_token')}`
        },
        body: JSON.stringify({ notification_ids: notificationIds })
      });

      if (response.ok) {
        setNotifications(prev => 
          prev.map(n => 
            notificationIds.includes(n.id) ? { ...n, read: true } : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - notificationIds.length));
      }
    } catch (error) { console.error(error); }
  }, []);

  // Clear all notifications via API
  const clearAll = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/notifications/clear-all', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('wms_access_token')}`
        }
      });

      if (response.ok) {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (error) { console.error(error); }
  }, []);

  return {
    notifications,
    unreadCount,
    connected,
    markAsRead,
    clearAll
  };
};

export default useRealTimeNotifications;

